import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { store } from "../app/store";
import { _post } from "../api/apiClient";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { CustomSuccess } from "../api/CustomSuccess";
import {
  setLoaderFalse,
  setLoaderTrue,
  setPerformaLoading,
} from "./loaderSlice";
import { current } from "immer";
import { getGraphObjectsFromInitialFilter } from "./newInitialFilterDataSlice";
import {
  setApplyButtonClicked,
  setEnableApplyButton,
} from "./selectedFilterSlice";
import { API_STATUS } from "../rtk-query/features/sliceUtil";
import { DASHBOARD_CARDS } from "../constants/dashboardCards";
import {
  CONSUMERS_TYPES,
  PRODUCT_OPPORTUNITIES,
} from "../constants/dashboardFilterInputs";

const CLUSTER_CARD_KEYS = {
  PRODUCT_OPPORTUNITIES: DASHBOARD_CARDS.PRODUCT_OPPORTUNITIES,
  DEPOSITIES: DASHBOARD_CARDS.DEPOSITIES,
  GHOST_CONSUMERS: DASHBOARD_CARDS.GHOST_CONSUMERS,
  TOP_MERCHANT: DASHBOARD_CARDS.TOP_MERCHANTS,
  TOP_VENDOR: DASHBOARD_CARDS.TOP_VENDORS,
  BANKING_SERVICES: DASHBOARD_CARDS.BANKING_SERVICES,
  PRIMARY_CHECKING_ACCOUNT: DASHBOARD_CARDS.PRIMARY_CHECKING_ACCOUNT,
};

const createSelector = (getter) => () => getter(store.getState());

export const _orgClientId = createSelector(
  (state) => state?.orgReducer?.selectedFI?.orgClientId,
);
export const _hid = createSelector(
  (state) => state?.orgReducer?.healthAnalysisID?.uid,
);
export const _FILTERS = createSelector(
  (state) => state?.newClusterReducer?.FILTERS,
);
export const _contextualFilterObj = createSelector(
  (state) => state?.newClusterReducer?.contextualFilterObj,
);
export const _userMailId = createSelector(
  (state) => state?.authReducer?.userData?.userName,
);
export const _currentDashboard = createSelector(
  (state) => state.dashboardReducer.currentDashboard,
);
export const _clusterApiKey = createSelector(
  (state) =>
    state?.dashboardReducer?.selectedCard?.clusterObject?.clusterApiKey,
);
export const _selectedCard = createSelector(
  (state) => state?.dashboardReducer?.selectedCard,
);

export const SORT_BY = {
  alphabetical: "alphabetical",
  numerical: "numerical",
};

export const SORT_ORDER = {
  ascending: "ascending",
  descending: "descending",
};

export const FILTER_KEYS = {
  balance: "balance",
  oppotype: "oppotype",
  deposit_option: "deposit_option",
  products: "products",
  product_with_provider: "product_with_provider",
  consumer: "consumer",
  persona: "persona",
  zonename: "zonename",
  consumertype: "consumertype",
  deposit_type: "deposit_type",
  depositType_with_provider: "product_with_provider",
  providers: "providers",
  category: "category",
  category_with_merchant: "category_with_merchant",
  merchant: "merchant",
};

const NUMERIC_DESC_SORT = {
  sortBy: SORT_BY.numerical,
  sortOrder: SORT_ORDER.descending,
};

const ALPHA_ASC_SORT = {
  sortBy: SORT_BY.alphabetical,
  sortOrder: SORT_ORDER.ascending,
};

const createBalanceFilter = (filterName) => ({
  key: FILTER_KEYS.balance,
  filterName,
});

const createSortedFilter = (key, filterName, sortConfig, extraConfig = {}) => ({
  key,
  filterName,
  ...sortConfig,
  ...extraConfig,
});

const createSubFilterObject = (config, defaults = {}) => ({
  key: config.key,
  filterName: config.filterName,
  postFix: config.postFix,
  subFilterName: config.subFilterName,
  subFilterKey: config.subFilterKey,
  subFilterInternalKey: config.subFilterInternalKey,
  subFilterDisplayKey: config.subFilterDisplayKey || "name",
  sortBy: config.sortBy,
  sortOrder: config.sortOrder,
  subFilterSortBy: config.subFilterSortBy,
  subFilterSortOrder: config.subFilterSortOrder,
  extendMenu: false,
  ...defaults,
});

export const createProductsWithProvidersFilter = ({
  key = FILTER_KEYS.products,
  filterName = "Products",
  postFix = "Providers",
  subFilterName = FILTER_KEYS.product_with_provider,
  subFilterKey = FILTER_KEYS.product_with_provider,
  subFilterInternalKey = "product",
  subFilterDisplayKey = "name",
  sortBy = SORT_BY.numerical,
  sortOrder = SORT_ORDER.descending,
  subFilterSortBy = SORT_BY.numerical,
  subFilterSortOrder = SORT_ORDER.descending,
} = {}) =>
  createSubFilterObject({
    key,
    filterName,
    postFix,
    subFilterName,
    subFilterKey,
    subFilterInternalKey,
    subFilterDisplayKey,
    sortBy,
    sortOrder,
    subFilterSortBy,
    subFilterSortOrder,
  });

export const createCategoryWithEntityFilter = ({ postFix = "Merchants" } = {}) =>
  createSubFilterObject({
    key: FILTER_KEYS.category,
    filterName: "Category",
    postFix,
    subFilterName: FILTER_KEYS.category_with_merchant,
    subFilterKey: FILTER_KEYS.category_with_merchant,
    subFilterInternalKey: "category",
    subFilterDisplayKey: "name",
    sortBy: SORT_BY.numerical,
    sortOrder: SORT_ORDER.descending,
    subFilterSortBy: SORT_BY.numerical,
    subFilterSortOrder: SORT_ORDER.descending,
  });

const createDepositTypeWithProvidersFilter = () =>
  createSubFilterObject({
    key: FILTER_KEYS.deposit_type,
    filterName: "External Transfers",
    postFix: "Providers",
    subFilterName: FILTER_KEYS.depositType_with_provider,
    subFilterKey: FILTER_KEYS.depositType_with_provider,
    subFilterInternalKey: "deposit",
    subFilterDisplayKey: "name",
    sortBy: SORT_BY.numerical,
    sortOrder: SORT_ORDER.descending,
    subFilterSortBy: SORT_BY.numerical,
    subFilterSortOrder: SORT_ORDER.descending,
  });

const createAudienceFilters = (sortConfig) => [
  {
    key: FILTER_KEYS.consumer,
    filterName: "Consumers",
    ...sortConfig,
  },
  {
    key: FILTER_KEYS.persona,
    filterName: "Personas",
    ...sortConfig,
  },
  {
    key: FILTER_KEYS.zonename,
    filterName: "Zones",
    sortBy: SORT_BY.alphabetical,
    sortOrder: SORT_ORDER.ascending,
  },
];

export const createNumericAudienceFilters = () =>
  createAudienceFilters(NUMERIC_DESC_SORT);
export const createAlphabeticalAudienceFilters = () =>
  createAudienceFilters(ALPHA_ASC_SORT);

const createGraphRequestConfig = (
  successMessage = "Fetched Graph data successfully.",
  errorMessage = "Failed to fetch Graph data!",
) => ({ successMessage, errorMessage });

const buildDrilldownUrl = (clusterApiKey, endpoint) =>
  `bank/${_orgClientId()}/drilldown/${_hid()}/${clusterApiKey}/${endpoint}`;

export const createIndexMap = (
  items,
  keyExtractor = (item) => item?.id,
  valueMapper = (item) => item,
) => new Map(items?.map((item) => [keyExtractor(item), valueMapper(item)]));

const resetApplyButtonState = (thunkAPI) => {
  thunkAPI.dispatch(setEnableApplyButton(false));
  thunkAPI.dispatch(setApplyButtonClicked(false));
};

export const buildDetailQueryString = (paginationObj = {}) => {
  const { currentPage, limit, sortObj } = paginationObj;
  const params = [];

  if (currentPage) params.push(`pageNumber=${currentPage}`);
  if (limit > 0) params.push(`PageSize=${limit}`);
  if (sortObj?.sortOrder) params.push(`orderBy=${sortObj.sortOrder}`);
  if (sortObj?.sortField) params.push(`sortBy=${sortObj.sortField}`);

  return params.length > 0 ? `?${params.join("&")}` : "?";
};

const buildDrilldownPayload = ({
  filter = getOnlyIdsForAPI(),
  extra = {},
} = {}) => ({
  filter,
  requid: _hid(),
  producttype: _currentDashboard()?.KEY,
  gte: "",
  lte: "",
  duration: "",
  ...extra,
});

const parseDrilldownSuccessResponse = (response, config) => {
  if (response?.statusCode !== HttpStatusCode.Ok) {
    throw CustomError(response, config);
  }

  return CustomSuccess(response, {
    ...config,
    disableToast: true,
  });
};

const postDrilldownAndParse = async ({
  clusterApiKey,
  endpoint,
  payload,
  config,
  apiName,
}) => {
  const response = await _post(buildDrilldownUrl(clusterApiKey, endpoint), payload, {
    metadata: { apiName },
  });
  return parseDrilldownSuccessResponse(response, config);
};

const runDrilldownRequest = async ({ endpoint, payload, config, apiName }) => {
  const clusterApiKey = _clusterApiKey();
  const successResult = await postDrilldownAndParse({
    clusterApiKey,
    endpoint,
    payload,
    config,
    apiName,
  });

  return {
    clusterApiKey,
    successResult,
  };
};

const getExportFileName = () => {
  const dashboardKey = _currentDashboard()?.KEY;
  const selectedCard = _selectedCard() || {};
  const fileTitle =
    dashboardKey === 2 ? selectedCard.commercial_title : selectedCard.retail_title;

  return fileTitle ? `${fileTitle} export data.csv` : "export data.csv";
};

const triggerBackgroundDownload = (fileUrl, fileName) => {
  if (typeof fileUrl !== "string" || !fileUrl.trim()) {
    throw new Error("The export response did not include a download URL.");
  }
  const parsedUrl = new URL(fileUrl);
  if (!["https:", "http:"].includes(parsedUrl.protocol)) {
    throw new Error("The export response included an invalid download URL.");
  }

  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = fileName;
  // Cross-origin downloads require Content-Disposition: attachment on the server.
  link.rel = "noreferrer";
  link.style.display = "none";

  try {
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
  }

  return fileUrl;
};

const withLoadingThunkExecution = async (
  thunkAPI,
  { setLoadingTrue, setLoadingFalse, request, onSuccess = (result) => result },
) => {
  try {
    thunkAPI.dispatch(setLoadingTrue());
    const result = await request();
    thunkAPI.dispatch(setLoadingFalse());
    return onSuccess(result);
  } catch (error) {
    thunkAPI.dispatch(setLoadingFalse());
    return thunkAPI.rejectWithValue(error);
  }
};

const createThunkWithLoader = (
  type,
  request,
  { setLoadingTrue = setLoaderTrue, setLoadingFalse = setLoaderFalse } = {},
) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    return withLoadingThunkExecution(thunkAPI, {
      setLoadingTrue,
      setLoadingFalse,
      request: () => request(arg, thunkAPI),
    });
  });

const setStateLoadingStatus = (state, key, isLoading, data = null) => {
  state[key] = {
    ...(data !== null && { data }),
    isLoading,
  };
};

const setLoadingState = (state, key) => setStateLoadingStatus(state, key, true);
const setLoadedState = (state, key, data) =>
  setStateLoadingStatus(state, key, false, data);
const setLoadFailedState = (state, key) =>
  setStateLoadingStatus(state, key, false);

const mapFilterEntries = (value, mapper) =>
  Array.isArray(value)
    ? value.map((entry, index, collection) => mapper(entry, index, collection))
    : undefined;

const mapAndAssignFilterGroup = (state, groupKey, mapper) => {
  const existingArray = current(state.FILTERS) || [];
  const updatedArray = existingArray?.[groupKey]?.map((item) => mapper(item));

  state.FILTERS = {
    ...state.FILTERS,
    [groupKey]: updatedArray,
  };
};

const createMappedEntry = (entry, config, extra = {}) => ({
  ...entry,
  childOf: config?.key,
  ...extra,
});

const addBasicThunkCases = (builder, thunk, stateKey, onFulfilled = null) => {
  builder
    .addCase(thunk.pending, (state) => {
      setLoadingState(state, stateKey);
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (onFulfilled) {
        onFulfilled(state, action);
      } else {
        setLoadedState(state, stateKey, action.payload?.data);
      }
    })
    .addCase(thunk.rejected, (state) => {
      setLoadFailedState(state, stateKey);
    });
};

const createMerchantLikeFilters = (
  balanceLabel,
  categoryPostFix,
  merchantLabel,
) => [
  createBalanceFilter(balanceLabel),
  createCategoryWithEntityFilter({ postFix: categoryPostFix }),
  createSortedFilter(FILTER_KEYS.merchant, merchantLabel, NUMERIC_DESC_SORT),
  ...createNumericAudienceFilters(),
];

const initialState = {
  graphData: null,
  detailData: null,
  filterData: null,
  initialFilterData: {
    data: null,
    isLoading: true,
  },
  selectedFilterData: {},
  contextualFilterObj: null,
  FILTERS: {
    product_opportunities: [
      createBalanceFilter("Checking Account Balance"),
      createSortedFilter(
        FILTER_KEYS.oppotype,
        "Opportunity Types",
        NUMERIC_DESC_SORT,
      ),
      createProductsWithProvidersFilter(),
      ...createNumericAudienceFilters(),
    ],
    deposit_opportunities: [
      createBalanceFilter("Checking Account Balance"),
      createSortedFilter(
        FILTER_KEYS.deposit_option,
        "Deposit Options",
        NUMERIC_DESC_SORT,
      ),
      createDepositTypeWithProvidersFilter(),
      createSortedFilter(
        FILTER_KEYS.providers,
        "Financial Institutions",
        NUMERIC_DESC_SORT,
        {
          rootExpandMenu: false,
          rootSearchPlaceholder: "Type institution name",
        },
      ),
      createSortedFilter(
        FILTER_KEYS.consumertype,
        "Consumers",
        NUMERIC_DESC_SORT,
      ),
      createSortedFilter(FILTER_KEYS.persona, "Personas", NUMERIC_DESC_SORT),
      createSortedFilter(FILTER_KEYS.zonename, "Zones", ALPHA_ASC_SORT),
    ],
    primary_checking_account: [
      createBalanceFilter("Checking Account Balance"),
      createProductsWithProvidersFilter({
        sortBy: SORT_BY.numerical,
        sortOrder: SORT_ORDER.descending,
        subFilterSortBy: SORT_BY.numerical,
        subFilterSortOrder: SORT_ORDER.descending,
      }),
      createSortedFilter(
        FILTER_KEYS.providers,
        "Financial Institutions",
        NUMERIC_DESC_SORT,
        {
          rootExpandMenu: false,
          rootSearchPlaceholder: "Type institution name",
        },
      ),
      // ...createNumericAudienceFilters(),
    ],
    // ghost_consumer: [
    //   createBalanceFilter("Checking Account Balance"),
    //   createProductsWithProvidersFilter({
    //     sortBy: SORT_BY.numerical,
    //     sortOrder: SORT_ORDER.descending,
    //     subFilterSortBy: SORT_BY.numerical,
    //     subFilterSortOrder: SORT_ORDER.descending,
    //   }),
    //   ...createNumericAudienceFilters(),
    // ],
    top_merchant: createMerchantLikeFilters(
      "Expenditure Amount",
      "Merchants",
      "Merchants",
    ),
    top_vendor: createMerchantLikeFilters(
      "Expenditure Amount",
      "Vendors",
      "Vendors",
    ),
    banking_services: [
      createBalanceFilter("Checking Account Balance"),
      createProductsWithProvidersFilter(),
      createSortedFilter(FILTER_KEYS.providers, "Merchants", NUMERIC_DESC_SORT),
      ...createNumericAudienceFilters(),
    ],
  },
  selectedZones: null,
  selectedFilterObject: null,
  isOnlyPredictive: false,
};

const createClusterObject = (
  key,
  defaultFilter,
  legendsKey,
  initialStateFilters,
  clusterApiKey = key,
) => ({
  key,
  clusterApiKey,
  filterGroup: initialStateFilters?.[key],
  defaultFilter,
  legendsKey,
  zonesKey: FILTER_KEYS.zonename,
});

export const CLUSTER_OBJECTS = {
  product_opportunities: createClusterObject(
    CLUSTER_CARD_KEYS.PRODUCT_OPPORTUNITIES,
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.PRODUCT_OPPORTUNITIES,
  ),
  deposit_opportunities: createClusterObject(
    CLUSTER_CARD_KEYS.DEPOSITIES,
    FILTER_KEYS.deposit_type,
    FILTER_KEYS.deposit_type,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.DEPOSITIES,
  ),
  primary_checking_account: createClusterObject(
    CLUSTER_CARD_KEYS.PRIMARY_CHECKING_ACCOUNT,
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.PRIMARY_CHECKING_ACCOUNT,
  ),
  // ghost_consumer: createClusterObject(
  //   CLUSTER_CARD_KEYS.GHOST_CONSUMERS,
  //   FILTER_KEYS.products,
  //   FILTER_KEYS.products,
  //   initialState.FILTERS,
  //   CLUSTER_CARD_KEYS.GHOST_CONSUMERS,
  // ),
  top_merchant: createClusterObject(
    CLUSTER_CARD_KEYS.TOP_MERCHANT,
    FILTER_KEYS.merchant,
    FILTER_KEYS.category,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.TOP_MERCHANT,
  ),
  top_vendor: createClusterObject(
    CLUSTER_CARD_KEYS.TOP_VENDOR,
    FILTER_KEYS.merchant,
    FILTER_KEYS.category,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.TOP_MERCHANT,
  ),
  banking_services: createClusterObject(
    CLUSTER_CARD_KEYS.BANKING_SERVICES,
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    CLUSTER_CARD_KEYS.BANKING_SERVICES,
  ),
};

export const getOnlyIdsForAPI = () => {
  const state = store.getState();
  const selectedFilterData = state?.newClusterReducer?.selectedFilterData;

  return Object.fromEntries(
    Object.entries(selectedFilterData || {})
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.map((item) => item.id) : value,
      ])
      .filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : typeof v === "object" && v !== null,
      ),
  );
};

function mapSimpleFilterValues(value, config) {
  return mapFilterEntries(value, (entry) => createMappedEntry(entry, config));
}

function mapNestedSubFilterValues(children, parentName, config) {
  return mapFilterEntries(children, (child) => ({
    ...child,
    childOf: parentName,
    rootFilter: config?.key,
    subFilterKey: config?.subFilterKey,
    subFilterDisplayKey: config?.subFilterDisplayKey,
  }));
}

function mapSubFilterValues(value, config) {
  return mapFilterEntries(value, (entry) => {
    const inputsFromConfig = [...PRODUCT_OPPORTUNITIES, ...CONSUMERS_TYPES]?.find(
      (item) => item?.key === entry?.name,
    );
    return createMappedEntry(entry, config, {
      postFix: config?.postFix,
      subFilterKey: config?.subFilterKey,
      subFilterName: config?.subFilterName,
      subFilterInternalKey: config?.subFilterInternalKey,
      extendMenu: false,
      inputsFromConfig,
      subFilterSortBy: config?.subFilterSortBy,
      subFilterSortOrder: config?.subFilterSortOrder,
      [config?.subFilterKey]: mapNestedSubFilterValues(
        entry[config?.subFilterKey],
        entry?.name,
        config,
      ),
    });
  });
}

export const transformFilterData = (inputArray, options = {}) => {
  const { filters = _FILTERS(), clusterApiKey = _clusterApiKey() } = options;
  const output = {};
  const processFilterKey = (key, value, config) => {
    if (key === FILTER_KEYS.balance) {
      return { ...config, data: value[0] };
    }
    if (config?.subFilterKey == null) {
      return { ...config, data: mapSimpleFilterValues(value, config) };
    }
    return { ...config, data: mapSubFilterValues(value, config) };
  };

  for (const sourceItem of inputArray) {
    if (!sourceItem || typeof sourceItem !== "object") {
      continue;
    }

    for (const [key, value] of Object.entries(sourceItem)) {
      const config = filters?.[clusterApiKey]?.find(
        (filterItem) => filterItem.key === key,
      );
      output[key] = processFilterKey(key, value, config);
    }
  }

  return output;
};

export const transformGraphData = async (data) => {
  if (!data?.[0]) return null;

  const { graphItems, zoneItems } = await getGraphObjectsFromInitialFilter();

  const graphMap = createIndexMap(
    graphItems?.data,
    ({ id }) => id,
    ({ id, name, color_code }) => ({ id, name, color: color_code }),
  );

  const zoneMap = createIndexMap(zoneItems?.data, (item) => item?.id);

  const transformZoneData = (zone) => {
    const updatedZoneData = (zone.data || []).map((item) => ({
      ...item,
      ...graphMap.get(item?.id),
      value: item?.count,
    }));

    return {
      ...zone,
      ...zoneMap.get(zone?.zone_id),
      data: updatedZoneData,
    };
  };

  return {
    ...data[0],
    zone: (data[0].zone || []).map(transformZoneData),
  };
};

export const fetchGraphApiThunk = createThunkWithLoader(
  "fetch_graph_api_thunk",
  async (_, thunkAPI) => {
    const config = createGraphRequestConfig();
    const { successResult } = await runDrilldownRequest({
      endpoint: "graph",
      apiName: "fetchGraphApiThunk",
      payload: buildDrilldownPayload({
        extra: {
          isContext: _contextualFilterObj() != null,
        },
      }),
      config,
    });

    const graphData = await transformGraphData(successResult?.data);

    resetApplyButtonState(thunkAPI);
    return {
      ...successResult,
      data: graphData,
    };
  },
);

export const fetchDetailApiThunk = createThunkWithLoader(
  "fetch_detail_api_thunk",
  async ({ paginationObj }, thunkAPI) => {
    const config = createGraphRequestConfig(
      "Fetched detail data successfully.",
      "Failed to fetch detail data!",
    );
    const query = buildDetailQueryString(paginationObj);

    const { successResult } = await runDrilldownRequest({
      endpoint: `detail${query}`,
      apiName: "fetchDetailApiThunk",
      payload: buildDrilldownPayload(),
      config,
    });

    resetApplyButtonState(thunkAPI);
    return {
      ...successResult,
      paginationObj,
    };
  },
);

export const exportFilteredDrilldownDataThunk = createThunkWithLoader(
  "export_filtered_drilldown_data",
  async () => {
    const config = createGraphRequestConfig(
      "Export file prepared successfully.",
      "Failed to download the export file. Please try again.",
    );
    const { successResult } = await runDrilldownRequest({
      endpoint: "export?offset=0&limit=0",
      apiName: "exportFilteredDrilldownDataThunk",
      payload: buildDrilldownPayload({
        filter: {
          ...getOnlyIdsForAPI(),
          username: _userMailId(),
        },
      }),
      config,
    });

    const fileUrl = successResult?.data;
    const fileName = getExportFileName();

    try {
      triggerBackgroundDownload(fileUrl, fileName);
    } catch (error) {
      throw CustomError({ message: error.message }, config);
    }

    return successResult;
  },
  {
    setLoadingTrue: () => setPerformaLoading(true),
    setLoadingFalse: () => setPerformaLoading(false),
  },
);

export const newClusterSlice = createSlice({
  name: "new_cluster_slice",
  initialState,
  reducers: {
    setSelectedFilterData: (state, action) => {
      state.selectedFilterData = {
        ...state.selectedFilterData,
        [action.payload.storeInKey]: action?.payload?.data,
      };
    },
    setReplaceAllFilterData: (state, action) => {
      const _data = {
        ...action?.payload?.data,
        filterId: action?.payload?.filterId,
      };
      state.selectedFilterData = _data;
    },
    setContextualFilterObj: (state, action) => {
      state.contextualFilterObj = action?.payload;
    },
    updateSortParameters: (state, action) => {
      const {
        clusterApiKey,
        filter,
        sortOrder,
        sortBy,
        subFilterSortOrder,
        subFilterSortBy,
        isSubFilterSortEvent,
      } = action.payload;
      if (clusterApiKey == null) {
        return;
      }
      mapAndAssignFilterGroup(state, clusterApiKey, (item) => {
        if (item.key !== filter) {
          return { ...item };
        }

        if (isSubFilterSortEvent) {
          return { ...item, subFilterSortOrder, subFilterSortBy };
        }

        return { ...item, sortOrder, sortBy };
      });
    },
    updateExpandedMenu: (state, action) => {
      const { filterKey, storeInKey, selectedCheckboxId, extendMenu } =
        action.payload;

      if (filterKey == null) {
        return;
      }

      mapAndAssignFilterGroup(state, filterKey, (item) => {
        if (item.subFilterKey != null && item.key === storeInKey) {
          return { ...item, extendMenu, expendMenuId: selectedCheckboxId };
        }

        return { ...item };
      });
    },

    updateRootExpandedMenu: (state, action) => {
      const { filterKey, storeInKey, extendMenu } = action.payload;

      if (filterKey == null) {
        return;
      }
      mapAndAssignFilterGroup(state, filterKey, (item) => {
        if (item.rootExpandMenu != null && item.key === storeInKey) {
          return { ...item, rootExpandMenu: extendMenu };
        }
        return { ...item };
      });
    },

    resetSelectedFilterData: (state) => {
      state.selectedFilterData = {};
      state.contextualFilterObj = null;
    },
    resetNewClusterReducerState: () => initialState,
    setSelectedZones: (state, action) => {
      state.selectedZones = action.payload;
    },
    setSelectedFilterObject: (state, action) => {
      state.selectedFilterObject = action.payload;
    },
    setSimpleState: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
  },

  extraReducers: (builder) => {
    addBasicThunkCases(builder, fetchGraphApiThunk, "graphData");
    builder
      .addCase(fetchDetailApiThunk.pending, (_state) => ({
        ..._state,
        detailData: {
          ..._state.detailData,
          detailDataIsLoading: API_STATUS.STATUS_LOADING,
        },
      }))
      .addCase(fetchDetailApiThunk.fulfilled, (_state, _action) => {
        const { currentPage, limit } = _action?.payload?.paginationObj || {};
        const offset = (currentPage - 1) * limit;
        const detailViewData = _action?.payload?.data?.map((item, index) => {
          return { ...item, srNo: offset + index + 1 };
        });
        return {
          ..._state,
          detailData: {
            data: detailViewData,
            totalCount: _action?.payload?.data?.[0]?.totalcount,
            detailDataIsLoading: API_STATUS.STATUS_SUCCESS,
          },
        };
      })
      .addCase(fetchDetailApiThunk.rejected, (_state, _action) => ({
        ..._state,
        detailData: {
          detailDataIsLoading: API_STATUS.STATUS_FAILURE,
          data: [],
          message:
            _action.error.message || "Error while fetching email campaigns",
        },
      }));
  },
});

export const {
  setSelectedFilterData,
  setReplaceAllFilterData,
  setContextualFilterObj,
  updateSortParameters,
  updateExpandedMenu,
  updateRootExpandedMenu,
  setSelectedZones,
  setSelectedFilterObject,
  resetSelectedFilterData,
  resetNewClusterReducerState,
} = newClusterSlice.actions;

export default newClusterSlice.reducer;
